import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
} from '@angular/core';

import { UrlFieldValidators } from 'src/app/components/url-field/url-field-validators';

import { AttributeDataService } from '../../services/attribute-data.service';
import { ComponentsService } from '../../services/components.service';
import {
  YoutubeValidationResult,
  YouTubeSourceType,
  YouTubeValidationService,
} from '../services/youtube-validation-service.service';
import { UserService } from 'src/app/settings/services/user.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';

import { environment } from 'src/environments/environment';

const SOURCE_TYPE_LABELS: Record<YouTubeSourceType, string> = {
  video: 'Video',
  playlist: 'Playlist',
  livestream: 'Live stream',
};

@Component({
  selector: 'template-component-youtube',
  templateUrl: './youtube.component.html',
  styleUrls: ['./youtube.component.scss'],
  standalone: false,
})
export class YouTubeComponent implements OnInit {
  spinner = false;
  // Shown in the Source input, so it stays editable while the source is validated
  validating = false;
  componentId: string;
  source: string;
  // The source that plays on the Display, which an edited or rejected input doesn't change
  private savedSource: string;
  volume: number;
  validationStatus: YoutubeValidationResult['status'];
  termsAccepted = false;
  sourceType: YouTubeSourceType;
  videoDuration: number;
  // Not saved as an attribute, since a playlist can change after it's set
  playlistLength: number;
  private sourceRequestId = 0;
  // Latest source request for each component, so older results are discarded
  private latestSourceRequests = new Map<string, number>();

  constructor(
    private elementRef: ElementRef,
    private componentsFactory: ComponentsService,
    private attributeDataService: AttributeDataService,
    private youTubeValidationService: YouTubeValidationService,
    private changeDetectorRef: ChangeDetectorRef,
    private userService: UserService,
    private userStateService: UserStateService,
  ) {
    componentsFactory.registerDirective({
      type: 'rise-youtube',
      element: this.elementRef.nativeElement,
      show: () => {
        this.componentId = this.componentsFactory.selected.id;

        this.load();
      },
    });
  }

  load() {
    this.source = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'source',
    );
    this.savedSource = this.source;
    this.volume =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'volume',
      ) || 0;
    this.sourceType = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'sourceType',
    );
    this.videoDuration = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'videoDuration',
    );
    this.playlistLength = undefined;
    // Don't carry the previous component's validation over to this one. A source
    // type is only saved for a valid source, so it shows as valid without checking it again.
    this.validationStatus =
      this.source && this.sourceType ? 'VALID' : undefined;
    this.validating = false;

    if (this.source && (!this.sourceType || this.sourceType === 'playlist')) {
      this.loadSourceDetails();
    }
  }

  // Loads the details that aren't saved: the playlist length, and the source
  // type and duration of sources saved before they were
  async loadSourceDetails() {
    const componentId = this.componentId;
    const source = this.source;
    const requestId = this.sourceRequestId;
    // A source without a saved type is checked like a new one, with the spinner
    const checkingSource = !this.sourceType;
    if (checkingSource) {
      this.validating = true;
    }

    const result = await this.youTubeValidationService.validate(source);

    // Ignore the result if the component or source changed, or the source was saved, while it was loading
    if (
      this.componentId !== componentId ||
      this.source !== source ||
      this.sourceRequestId !== requestId
    ) {
      // The source was changed without saving it, like to an invalid URL, so nothing else stops the spinner
      if (
        checkingSource &&
        this.componentId === componentId &&
        this.sourceRequestId === requestId
      ) {
        this.validating = false;
        this.changeDetectorRef.detectChanges();
      }
      return;
    }

    // Only shown, not saved, so opening the component doesn't change the presentation
    if (checkingSource) {
      this.sourceType = result.sourceType;
      this.videoDuration = result.videoDuration;
      this.validationStatus = result.status;
      this.validating = false;
    }
    if (result.sourceType === 'playlist') {
      this.playlistLength = result.playlistLength;
    }
    this.changeDetectorRef.detectChanges();
  }

  get sourceTypeLabel() {
    return SOURCE_TYPE_LABELS[this.sourceType];
  }

  // Settings like volume apply to the saved source, which keeps playing whether or not
  // its check succeeded (e.g. when the YouTube API fails). Hidden while a source is checked.
  get showSourceSettings() {
    return !!this.savedSource && !this.validating;
  }

  ngOnInit(): void {
    this.termsAccepted =
      !!this.userStateService.getCopyOfProfile().settings?.youtubeTermsAccepted;
  }

  acceptTerms() {
    this.spinner = true;
    this.userService
      .updateUser(this.userStateService.getUsername(), {
        settings: {
          youtubeTermsAccepted: true,
        },
      })
      .then(() => {
        this.termsAccepted = true;
      })
      .finally(() => {
        this.spinner = false;
      });
  }

  isValidYouTubeUrl(url: string) {
    /**
     * JavaScript function to match (and return) the video Id
     * of any valid Youtube Url, given as input string.
     * @author: Stephan Schmitz <eyecatchup@gmail.com>
     * @url: https://stackoverflow.com/a/10315969/624466
     */
    const urlWithVideoId =
      /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|live\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
    // Shorts IDs must be exactly 11 characters, followed only by a query or fragment
    const urlWithShortsId =
      /^(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/((\w|-){11})(?:[?#]\S*)?$/;
    const urlWithPlaylistId =
      /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:playlist\?list=|playlist\?.+&list=))(?:\S+)?$/;
    const urlWithUserHandle =
      /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(@[^?#&]+))(?:\S+)?$/;

    return (
      urlWithVideoId.test(url) ||
      urlWithShortsId.test(url) ||
      urlWithPlaylistId.test(url) ||
      urlWithUserHandle.test(url)
    );
  }

  validateSource() {
    //clear the error
    this.validationStatus = undefined;

    const _source = !this.source ? '' : this.source.trim();
    if (_source === '') {
      //empty string is allowed
      return true;
    }

    if (UrlFieldValidators.isInsecureHttpUrl(_source)) {
      this.source = _source.replace('http://', 'https://');
    }

    if (this.isValidYouTubeUrl(_source)) {
      return true;
    }

    this.validationStatus = 'INVALID';
    return false;
  }

  async saveSource() {
    // The input also changes when a component is opened, with the source it already has.
    // Only an input rejected before saving needs that source saved again, to show its details.
    if (
      this.source === this.savedSource &&
      this.validationStatus !== 'INVALID'
    ) {
      return;
    }

    if (this.validateSource()) {
      this.saveEnvironment();

      this.attributeDataService.setAttributeData(
        this.componentId,
        'source',
        this.source,
      );
      this.savedSource = this.source;
      // Until the new source is validated, its details are unknown. Saved as such, so the
      // presentation never pairs it with the previous source's type and duration.
      this.attributeDataService.setAttributeData(
        this.componentId,
        'sourceType',
        null,
      );
      this.attributeDataService.setAttributeData(
        this.componentId,
        'videoDuration',
        null,
      );

      // Results apply to the component and source that started the request
      const requestId = ++this.sourceRequestId;
      const componentId = this.componentId;
      const source = this.source;
      this.latestSourceRequests.set(componentId, requestId);

      this.sourceType = undefined;
      this.videoDuration = undefined;
      this.playlistLength = undefined;
      this.validating = true;
      this.changeDetectorRef.detectChanges();

      const result = await this.youTubeValidationService.validate(source);

      // A newer save for this component replaced this one, and will set the attributes itself
      if (this.latestSourceRequests.get(componentId) !== requestId) {
        return;
      }

      if (result.data?.kind === 'youtube#channel') {
        this.attributeDataService.setAttributeData(
          componentId,
          'channelId',
          result.data?.id,
        );
        this.attributeDataService.setAttributeData(
          componentId,
          'thumbnailUrl',
          result.data?.snippet?.thumbnails?.high?.url,
        );
      } else {
        this.attributeDataService.setAttributeData(
          componentId,
          'channelId',
          null,
        );
        this.attributeDataService.setAttributeData(
          componentId,
          'thumbnailUrl',
          null,
        );
      }

      const sourceType = result.sourceType || null;
      const videoDuration = result.videoDuration || null;
      this.attributeDataService.setAttributeData(
        componentId,
        'sourceType',
        sourceType,
      );
      this.attributeDataService.setAttributeData(
        componentId,
        'videoDuration',
        videoDuration,
      );

      // Only show the result if the same component and source are still open
      if (this.componentId === componentId && this.source === source) {
        this.sourceType = sourceType;
        this.videoDuration = videoDuration;
        this.playlistLength = result.playlistLength;
        this.validationStatus = result.status;
      }

      // Keep the spinner while a newer save is still validating
      if (requestId === this.sourceRequestId) {
        this.validating = false;
      }
      this.changeDetectorRef.detectChanges();
    } else {
      // The saved source is unchanged, but its details no longer match the input
      this.sourceType = undefined;
      this.videoDuration = undefined;
      this.playlistLength = undefined;
    }
  }

  saveVolume() {
    this.saveEnvironment();
    this.attributeDataService.setAttributeData(
      this.componentId,
      'volume',
      this.volume,
    );
  }

  saveEnvironment() {
    this.attributeDataService.setAttributeData(
      this.componentId,
      'environment',
      environment.production ? 'prod' : 'test',
    );
  }
}
